// Delivery zones and fees come from the server (GET /settings/shipping/zones,
// the table POST /orders charges from). The checkout shows an estimate from
// it; the order response is the truth. Never hard-code fees here.

import { API_URL, memoPublic } from './api';
import { FACTS, readFacts, writeFacts } from './factsCache';
import { groupDigits } from './vat';

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const normalizeZones = (data) => (Array.isArray(data) ? data : [])
  .filter((z) => z && typeof z === 'object' && String(z.zone || '').trim())
  .map((z) => ({
    zone: String(z.zone).trim(),
    fee: Math.max(0, num(z.fee) ?? 0),
    freeThreshold: num(z.free_threshold),
    estimatedDays: num(z.estimated_days),
  }));

const requestZones = async (fetchImpl, remember = false) => {
  const response = await fetchImpl(`${API_URL}/settings/shipping/zones`);
  if (!response.ok) throw new Error(`zones ${response.status}`);
  const data = await response.json();
  if (remember && Array.isArray(data)) writeFacts(FACTS.zones, data);
  return normalizeZones(data);
};

/** The last zone table (≤ 10 min old, utils/factsCache) for a first paint, or
 *  null. Shop copy only: checkout's estimate asks the server (fetchShippingZones). */
export const cachedShippingZones = () => {
  const data = readFacts(FACTS.zones);
  return Array.isArray(data) ? normalizeZones(data) : null;
};

// Without `fetchImpl` the answer is shared (utils/api memoPublic) by the
// facts provider, the product page and checkout: one request per minute. A
// failure is not kept, so checkout's retry asks the server again.
export const fetchShippingZones = async (fetchImpl) => {
  try {
    if (fetchImpl) return await requestZones(fetchImpl);
    return await memoPublic('facts:zones', () => requestZones(fetch, true));
  } catch {
    return [];
  }
};

const norm = (name) => String(name || '').toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();

// Emirate names in the address form ↔ short zone names the server may use.
const EMIRATE_ALIASES = {
  'umm al quwain': ['uaq'],
  'ras al khaimah': ['rak'],
  'abu dhabi': ['auh'],
  dubai: ['dxb'],
  sharjah: ['shj'],
};

/** The zone covering `emirate` (by name or a known alias), or null. */
export const zoneForEmirate = (zones, emirate) => {
  const wanted = norm(emirate);
  if (!wanted) return null;
  const names = [wanted, ...(EMIRATE_ALIASES[wanted] || [])];
  return (zones || []).find((z) => names.includes(norm(z.zone))) || null;
};

/** Mirrors utils/pricing.compute_delivery_fee: free at/above the threshold. */
export const deliveryFeeFor = (zone, netAmount) => {
  if (!zone || zone.fee <= 0) return 0;
  const threshold = zone.freeThreshold;
  if (threshold != null && threshold > 0 && netAmount >= threshold) return 0;
  return zone.fee;
};

const ZONE_LABELS = {
  international: { en: 'International', ar: 'دولي' },
  uaq: { en: 'Umm Al Quwain', ar: 'أم القيوين' },
  rak: { en: 'Ras Al Khaimah', ar: 'رأس الخيمة' },
  'abu dhabi': { en: 'Abu Dhabi', ar: 'أبوظبي' },
  dubai: { en: 'Dubai', ar: 'دبي' },
  sharjah: { en: 'Sharjah', ar: 'الشارقة' },
  ajman: { en: 'Ajman', ar: 'عجمان' },
  fujairah: { en: 'Fujairah', ar: 'الفجيرة' },
};

/** {en, ar} label for a server zone (unknown names shown as they are). */
export const zoneLabel = (zone) => ZONE_LABELS[norm(zone?.zone)] || { en: zone?.zone || '', ar: zone?.zone || '' };

// ── Shop delivery promises (header, home, product page, bag) ─────────────
// Built only from the zone table, like backend routes/content.delivery_facts:
// never "same-day" or a threshold the zones do not support.

const NON_UAE = new Set(['international', 'gcc']);

/** Zones that deliver inside the UAE. */
export const uaeZones = (zones) => (zones || []).filter((z) => z && !NON_UAE.has(norm(z.zone)));

/**
 * {freeOver, freeOverExVat, allFree, minDays, maxDays} for UAE delivery.
 * `freeOverExVat` is set only when every paid UAE zone has a free threshold,
 * and is the highest one, so "free over X" is true for every emirate; the
 * server compares it with the goods excl. VAT, so `freeOver` (via
 * `withVatFn`) is the same amount on the VAT-inclusive prices the shop
 * shows. Days are the zones' estimates. null without UAE zones.
 */
export const uaeDeliveryFacts = (zones, withVatFn = (x) => x) => {
  const uae = uaeZones(zones);
  if (!uae.length) return null;
  const paid = uae.filter((z) => z.fee > 0);
  const thresholds = paid.map((z) => z.freeThreshold);
  const freeOverExVat = paid.length && thresholds.every((th) => th != null && th > 0) ? Math.max(...thresholds) : null;
  const days = uae.map((z) => z.estimatedDays).filter((d) => Number.isFinite(d) && d > 0);
  return {
    freeOverExVat,
    freeOver: freeOverExVat == null ? null : withVatFn(freeOverExVat),
    allFree: paid.length === 0,
    minDays: days.length ? Math.min(...days) : null,
    maxDays: days.length ? Math.max(...days) : null,
  };
};

const amountText = (value) => groupDigits(value, 'trim');

/** {en, ar} "Free UAE delivery over 525 AED", or null when the zones don't support it. */
export const freeDeliveryText = (facts, approx) => {
  if (!facts) return null;
  if (facts.allFree) return { en: 'Free delivery across the UAE', ar: 'توصيل مجاني داخل الإمارات' };
  if (facts.freeOver == null) return null;
  // approx (display currency, utils/displayCurrency): "≈ SAR 536 (525 AED)".
  if (approx) {
    const x = approx(facts.freeOver);
    return { en: `Free UAE delivery over ${x}`, ar: `توصيل مجاني داخل الإمارات للطلبات فوق ${x}` };
  }
  return {
    en: `Free UAE delivery over ${amountText(facts.freeOver)} AED`,
    ar: `توصيل مجاني داخل الإمارات للطلبات فوق ${amountText(facts.freeOver)} درهم`,
  };
};

/** {en, ar} "Delivery in 1–2 days across the UAE", or null without estimates. */
export const deliveryTimeText = (facts) => {
  if (!facts || facts.minDays == null) return null;
  const { minDays: lo, maxDays: hi } = facts;
  const range = lo === hi ? String(lo) : `${lo}–${hi}`;
  return {
    en: `Delivery in ${range} ${hi === 1 ? 'day' : 'days'} across the UAE`,
    ar: `التوصيل خلال ${range} ${hi === 1 ? 'يوم' : 'أيام'} داخل الإمارات`,
  };
};

/** Copy for when the zones could not be loaded: no numbers. */
export const GENERIC_DELIVERY_TEXT = Object.freeze({
  en: 'Delivery across the UAE, with fees and timing shown at checkout',
  ar: 'التوصيل داخل الإمارات، وتظهر الرسوم والمدة عند الدفع',
});

/** {en, ar} return promise that matches the policy (window + exclusions). */
export const returnsText = (days) => ({
  en: `Returns within ${days} days of delivery. Some exclusions apply`,
  ar: `الإرجاع خلال ${days} يومًا من التسليم، مع بعض الاستثناءات`,
});
